import { MemCommonMentionKind, MemCommonRichTextInputInstance } from "@mem-labs/common-editor";
import { PropsWithChildren } from "react";
import { PopoverProps } from "react-aria-components";
import { JsonObject } from "type-fest";
import { MDSIconKind } from "@/mds/components/icon";

export enum MDSComboboxMode {
  Default = "default",
  Passive = "passive",
}

// Slot names for customization
export enum MDSComboboxSlotName {
  LeftAddon = "left",
  RightAddon = "right",
  ActionButton = "action",
}

/** Configuration for a single option in the bottom dropdown */
export interface MDSComboboxDropdownOption {
  /** Unique identifier for the option */
  id: string;
  /** Display label for the option */
  label: string;
  /** Optional icon to display */
  icon?: MDSIconKind;
  /** Whether this option is in a "premium" section (shows badge) */
  showBadge?: boolean;
  /** Custom badge label (defaults to "Pro") */
  badgeLabel?: string;
}

/** Configuration for the dropdown in the combobox bottom row */
export interface MDSComboboxDropdownConfig {
  /** List of available options */
  options: MDSComboboxDropdownOption[];
  /** Currently selected option ID */
  selectedId: string;
  /** Called when an option is selected */
  onSelect: (optionId: string) => void;
  /** Optional: index after which to show a divider (e.g., 0 means divider after first item) */
  dividerAfterIndex?: number;
  /** Optional section header text rendered after the first (selected) item */
  sectionHeader?: string;
  /** Called when the dropdown opens or closes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Optional footer content rendered below the options with a divider */
  footer?: React.ReactNode;
  /** Optional fixed dropdown width in pixels (defaults to 176). */
  dropdownWidthPx?: number;
  /** Dropdown overlay mode: "implicit" renders non-modal (no blocking underlay) */
  dropdownMode?: "implicit" | "explicit";
  /** Option ID whose icon should be hidden in the trigger button (e.g., "Auto" model) */
  defaultOptionId?: string;
}

/**
 * Voice capture state. When present the button row swaps to cancel + waveform
 * + confirm, and the container takes the emphasis border.
 */
export interface MDSComboboxVoiceConfig {
  /** Current input level, 0..1. Drives the newest waveform bar. */
  amplitude: number;
  /** Capture has stopped and the transcript is still resolving. */
  isFinalizing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export enum MDSComboboxVariant {
  Default = "default",
  Brand = "brand",
  Emphasis = "emphasis",
  Chrome = "chrome",
}

// State interface for the combobox reducer
export interface ComboboxState<T> {
  isOpen: boolean;
  query: string;
  isEmpty: boolean;
  isLoading: boolean;
  data: T[];
  hasSearched: boolean;
  highlightedItemId: string | null;
  mentions: MDSComboboxMention[];
  popoverMentionMode: "implicit" | "explicit";
  isExpanded: boolean;
}

// Action types for the combobox reducer
export type ComboboxAction<T> =
  | { type: "QUERY_CHANGED"; payload: string }
  | { type: "SEARCH_STARTED" }
  | { type: "SEARCH_COMPLETED"; payload: T[] }
  | { type: "SEARCH_FAILED" }
  | { type: "LOADING_STARTED" }
  | { type: "LOADING_STOPPED" }
  | { type: "POPOVER_OPENED" }
  | { type: "POPOVER_CLOSED" }
  | { type: "EMPTY_STATE_CHANGED"; payload: boolean }
  | { type: "HIGHLIGHT_CHANGED"; payload: string | null }
  | { type: "HIGHLIGHT_NEXT" }
  | { type: "HIGHLIGHT_PREVIOUS" }
  | { type: "RESET" }
  | { type: "POPOVER_MENTION_MODE_CHANGED"; payload: "implicit" | "explicit" }
  | { type: "SET_EXPANDED"; payload: boolean };

export type MDSComboboxMention = {
  kind: MemCommonMentionKind;
  isExternal: boolean;
} & (
  | {
      kind: MemCommonMentionKind.Collection;
      collection_id: string;
      collection_title: string;
    }
  | {
      kind: MemCommonMentionKind.Note;
      note_id: string;
      note_primary_label: string;
    }
);

export type MDSComboboxFetchDataHandler<T extends MDSComboboxItem> = ({
  query,
  mentionSymbol,
}: {
  query: string;
  mentionSymbol?: string;
}) => Promise<MDSComboboxDataSource<T>>;

export type MDSComboboxSubmitHandler = (payload: {
  tiptapJsonContent: JsonObject;
  mdxContent: string;
  htmlContent: string;
  mentions: MDSComboboxMention[];
  implicitMentions?: MDSComboboxMention[];
}) => Promise<boolean>;

export interface MDSComboboxProps<T extends MDSComboboxItem> extends PropsWithChildren {
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  chatIsClosed?: boolean;
  popoverProps?: PopoverProps;
  autoFocus?: boolean;
  fetchData: MDSComboboxFetchDataHandler<T>;
  onSubmit?: MDSComboboxSubmitHandler;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputChange?: (height?: number) => void;
  onDisabledClick?: () => void;
  inputRef?: React.RefObject<MemCommonRichTextInputInstance>;
  mode?: MDSComboboxMode;
  editorDimensions?: {
    minHeight?: number;
    maxHeight?: number;
    padding?: string;
  };
  setContent?: string;
  onContentModified?: () => void;
  /** Reports the editor's HTML whenever its content may have changed. */
  onContentHtmlChanged?: (html: string) => void;
  className?: string;
  variant?: MDSComboboxVariant;
  fetchIsInstant?: boolean;
  mentions: MDSComboboxMention[];
  onMentionAdd?: (mention: MDSComboboxMention) => void;
  onMentionRemove?: (mentionId: string) => void;
  onMentionsClear?: () => void;
  implicitMentions?: MDSComboboxMention[];
  forceClose?: boolean;
  /** When true, forces the combobox to collapse regardless of focus or content state */
  forceCollapse?: boolean;
  onLeftAddonButtonClick?: () => void;
  /** Configuration for the bottom dropdown. When provided, shows dropdown in the bottom row. */
  dropdownConfig?: MDSComboboxDropdownConfig;
  /** When provided, the composer renders its voice-capture row instead of the usual controls. */
  voice?: MDSComboboxVoiceConfig;
}

export type MDSComboboxDataSource<T extends MDSComboboxItem> = {
  data: T[];
  error?: string;
  isLoading?: boolean;
};

export interface MDSComboboxItem {
  id: string;
  label: string;
  value?: string;
  disabled?: boolean;
  isHeader?: boolean;
  kind: MemCommonMentionKind | "create-new-collection";
  metadata?: Record<string, unknown>;
  onClick?: () => Promise<string>;
}
